import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import dibsCss from 'dibs-css';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Badge } from 'dibs-elements/exports/Badge.tsx';
import { priceEstimatePill } from '../../pdpMessages.tsx';
import { useAppSelector } from '../../hooks/useReactReduxHooks.ts';
import { useSimilarSoldItemsTracking } from '../../hooks/useSimilarSoldItemsTracking.ts';
import { useShouldShowSimilarSoldItems } from '../../hooks/useShouldShowSimilarSoldItems.ts';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import { type PdpSharedEstimateValueBadge_viewer$key } from './__generated__/PdpSharedEstimateValueBadge_viewer.graphql.ts';
import { type PdpSharedEstimateValueBadge_item$key } from './__generated__/PdpSharedEstimateValueBadge_item.graphql.ts';

type PdpSharedEstimateValueBadgeProps = {
    viewer: PdpSharedEstimateValueBadge_viewer$key;
    item: PdpSharedEstimateValueBadge_item$key;
    currency: string;
    onClick?: () => void;
};

const viewerFragment = graphql`
    fragment PdpSharedEstimateValueBadge_viewer on Viewer {
        ...useShouldShowSimilarSoldItems_viewer
        ...useSimilarSoldItemsTracking_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpSharedEstimateValueBadge_item on Item {
        ...useShouldShowSimilarSoldItems_item
    }
`;

export const PdpSharedEstimateValueBadge: FC<PdpSharedEstimateValueBadgeProps> = ({
    viewer: viewerRef,
    item: itemRef,
    currency,
    onClick,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const { trackEstimateValueBadgeClick, trackEstimateValueBadgeImpression } =
        useSimilarSoldItemsTracking({ viewer });
    const { afterDisplay } = useAppSelector(relay => relay?.relayVariables?.variables || {});
    const { isBelowMinPriceEstimate } = useShouldShowSimilarSoldItems({
        viewer,
        item,
        currency,
        afterDisplay,
    });

    return (
        <VisibilityTracker
            onVisibilityChange={({ isVisible }) => {
                if (isVisible) {
                    trackEstimateValueBadgeImpression({ belowEstimate: isBelowMinPriceEstimate });
                }
            }}
            elementType="span"
            className={dibsCss.mlXxsmall}
        >
            <Link
                href="#"
                className={dibsCss.textSuccesssecondary}
                onClick={e => {
                    e.preventDefault();
                    trackEstimateValueBadgeClick({ belowEstimate: isBelowMinPriceEstimate });
                    onClick?.();
                }}
            >
                <Badge type="pill" size="small-heavy" backgroundColor="green-50">
                    {priceEstimatePill({ isBelowMinPriceEstimate })}
                </Badge>
            </Link>
        </VisibilityTracker>
    );
};
